/* =================================================================
   LOOKBOOK — interactive behavior
   ================================================================= */

(() => {
  const init = () => {

    /* -----------------------------------------------------------
       Reveal on scroll
       ----------------------------------------------------------- */
    const revealEls = document.querySelectorAll('[data-lb-reveal]');
    if (revealEls.length) {
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const delay = entry.target.getAttribute('data-lb-reveal-delay') || 0;
            entry.target.style.setProperty('--lb-reveal-delay', delay);
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

      revealEls.forEach((el) => revealObserver.observe(el));
    }

    /* -----------------------------------------------------------
       Look visibility (for image scale-in)
       ----------------------------------------------------------- */
    const looks = document.querySelectorAll('[data-lb-look]');
    if (looks.length) {
      const lookObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            lookObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      looks.forEach((l) => lookObserver.observe(l));
    }

    /* -----------------------------------------------------------
       Hotspot toggles
       ----------------------------------------------------------- */
    const closeAllHotspotCards = () => {
      document.querySelectorAll('[data-lb-hotspot-card]').forEach((card) => {
        card.setAttribute('hidden', '');
      });
      document.querySelectorAll('[data-lb-hotspot]').forEach((btn) => {
        btn.classList.remove('is-active');
        btn.setAttribute('aria-expanded', 'false');
      });
    };

    document.querySelectorAll('[data-lb-hotspot]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const cardId = btn.getAttribute('aria-controls');
        const card = document.getElementById(cardId);
        if (!card) return;

        const wasOpen = !card.hasAttribute('hidden');
        closeAllHotspotCards();

        if (!wasOpen) {
          card.removeAttribute('hidden');
          btn.classList.add('is-active');
          btn.setAttribute('aria-expanded', 'true');
        }
      });

      // Sync with product list item on hover
      const look = btn.closest('[data-lb-look]');
      if (look) {
        const hotspotIndex = Array.from(look.querySelectorAll('[data-lb-hotspot]')).indexOf(btn) + 1;
        const productLink = look.querySelector(`[data-lb-product-link="${hotspotIndex}"]`);

        if (productLink) {
          const highlight = () => productLink.classList.add('is-highlighted');
          const unhighlight = () => productLink.classList.remove('is-highlighted');

          btn.addEventListener('mouseenter', highlight);
          btn.addEventListener('mouseleave', unhighlight);
          btn.addEventListener('focus', highlight);
          btn.addEventListener('blur', unhighlight);
        }
      }
    });

    // Product list -> hotspot sync (reverse direction)
    document.querySelectorAll('[data-lb-product-link]').forEach((link) => {
      const idx = link.getAttribute('data-lb-product-link');
      const look = link.closest('[data-lb-look]');
      if (!look) return;

      const hotspot = look.querySelectorAll('[data-lb-hotspot]')[parseInt(idx, 10) - 1];
      if (!hotspot) return;

      link.addEventListener('mouseenter', () => hotspot.classList.add('is-active'));
      link.addEventListener('mouseleave', () => {
        // only remove if the card isn't open
        const card = document.getElementById(hotspot.getAttribute('aria-controls'));
        if (card && card.hasAttribute('hidden')) hotspot.classList.remove('is-active');
      });
    });

    // Close card button
    document.querySelectorAll('[data-lb-hotspot-close]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeAllHotspotCards();
      });
    });

    // Close cards on outside click
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-lb-hotspot]') || e.target.closest('[data-lb-hotspot-card]')) return;
      closeAllHotspotCards();
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAllHotspotCards();
    });

    /* -----------------------------------------------------------
       Scroll-to-next from hero
       ----------------------------------------------------------- */
    document.querySelectorAll('[data-lb-scroll-to-next]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const hero = btn.closest('[data-lb-hero]');
        const section = hero?.closest('.shopify-section');
        const next = section?.nextElementSibling;
        if (next) {
          next.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollBy({ top: window.innerHeight, behavior: 'smooth' });
        }
      });
    });

    /* -----------------------------------------------------------
       Subtle parallax on hero image (mousemove)
       ----------------------------------------------------------- */
    document.querySelectorAll('[data-lb-hero]').forEach((hero) => {
      const media = hero.querySelector('.lb-hero__media');
      if (!media || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      let raf = null;
      hero.addEventListener('mousemove', (e) => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const rect = hero.getBoundingClientRect();
          const x = (e.clientX - rect.left) / rect.width - 0.5;
          const y = (e.clientY - rect.top) / rect.height - 0.5;
          media.style.transform = `translate(${x * -12}px, ${y * -12}px)`;
        });
      });

      hero.addEventListener('mouseleave', () => {
        media.style.transform = '';
      });
    });

    /* -----------------------------------------------------------
       LOOKBOOK GRID — lightbox + keyboard nav
       ----------------------------------------------------------- */
    document.querySelectorAll('[data-lbg]').forEach((section) => {
      if (section.dataset.lbgBound === '1') return;
      section.dataset.lbgBound = '1';

      const enableLb = section.getAttribute('data-lbg-lightbox') === 'true';
      if (!enableLb) return;

      const lb = section.querySelector('[data-lbg-lb]');
      if (!lb) return;

      const mediaSlot = lb.querySelector('[data-lbg-lb-media]');
      const infoSlot = lb.querySelector('[data-lbg-lb-info]');
      const currentLabel = lb.querySelector('[data-lbg-lb-current]');
      const totalLabel = lb.querySelector('[data-lbg-lb-total]');
      const items = Array.from(section.querySelectorAll('[data-lbg-item]'));
      const openable = items.filter((it) => it.querySelector('[data-lbg-data]'));

      if (!openable.length) return;
      if (totalLabel) totalLabel.textContent = String(openable.length).padStart(2, '0');

      let currentIndex = -1;
      let lastFocus = null;

      const render = (idx) => {
        const item = openable[idx];
        if (!item) return;
        const tpl = item.querySelector('[data-lbg-data]');
        if (!tpl) return;

        const frag = tpl.content.cloneNode(true);
        const slideSet = frag.querySelector('.lbg-lb__slide-set');
        const infoSet = frag.querySelector('.lbg-lb__info-set');

        mediaSlot.innerHTML = '';
        infoSlot.innerHTML = '';

        if (slideSet) {
          const track = document.createElement('div');
          track.className = 'lbg-lb__media-track';
          while (slideSet.firstChild) track.appendChild(slideSet.firstChild);
          mediaSlot.appendChild(track);
        }
        if (infoSet) {
          while (infoSet.firstChild) infoSlot.appendChild(infoSet.firstChild);
        }

        currentIndex = idx;
        if (currentLabel) currentLabel.textContent = item.dataset.lbgNumber || String(idx + 1).padStart(2, '0');
      };

      const open = (idx) => {
        lastFocus = document.activeElement;
        render(idx);
        lb.hidden = false;
        lb.setAttribute('aria-hidden', 'false');
        requestAnimationFrame(() => lb.classList.add('is-open'));
        document.body.classList.add('lbg-lb-open');
        const closeBtn = lb.querySelector('[data-lbg-close]');
        if (closeBtn) closeBtn.focus({ preventScroll: true });
      };

      const close = () => {
        lb.classList.remove('is-open');
        lb.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('lbg-lb-open');
        setTimeout(() => {
          lb.hidden = true;
          mediaSlot.innerHTML = '';
          infoSlot.innerHTML = '';
          if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
        }, 480);
      };

      const next = () => render((currentIndex + 1) % openable.length);
      const prev = () => render((currentIndex - 1 + openable.length) % openable.length);

      openable.forEach((item, i) => {
        const trigger = item.querySelector('[data-lbg-open]');
        if (!trigger) return;
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          open(i);
        });
      });

      lb.querySelector('[data-lbg-close]')?.addEventListener('click', close);
      lb.querySelector('[data-lbg-next]')?.addEventListener('click', next);
      lb.querySelector('[data-lbg-prev]')?.addEventListener('click', prev);

      lb.addEventListener('click', (e) => {
        if (e.target === lb) close();
      });

      document.addEventListener('keydown', (e) => {
        if (lb.hidden) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowRight') next();
        else if (e.key === 'ArrowLeft') prev();
      });
    });

  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init when Shopify theme editor re-renders a section
  document.addEventListener('shopify:section:load', init);
})();
